/**
 * Хук для записи голосового комментария механика.
 * 
 * Управляет записью аудио, отправкой на сервер,
 * получением транскрипции, которая становится заключением механика.
 * 
 * Применяется: Diagnostic
 */

import { useState, useRef, useEffect } from 'react'
import { formatSeconds } from '../../../shared/lib/time'

export function useVoiceRecorder(token, orderId, onResult) {
    const [isRecording, setIsRecording] = useState(false)
    const [audioLevels, setAudioLevels] = useState(() => Array(25).fill(0.06))
    const recorderRef = useRef(null)
    const startingRef = useRef(false)
    const [isStartingRecording, setIsStartingRecording] = useState(false)
    const [recordingError, setRecordingError] = useState('')
    const [recordingSeconds, setRecordingSeconds] = useState(0)
    const [processingAudio, setProcessingAudio] = useState(false)
    const [audioUrl, setAudioUrl] = useState(null)
    const audioRef = useRef(null)
    const recordingTimerRef = useRef(null)
    const activeRef = useRef(true)
    const requestRef = useRef(null)

    useEffect(() => {
        if (!isRecording || !recorderRef.current) return
        const AudioContextClass = window.AudioContext || window.webkitAudioContext
        if (!AudioContextClass) return
        let context
        let source
        let frame
        let disposed = false
        try {
            context = new AudioContextClass()
            const analyser = context.createAnalyser()
            analyser.fftSize = 1024
            source = context.createMediaStreamSource(recorderRef.current.stream)
            source.connect(analyser)
            // Do not connect to speakers: the microphone must not play back live.
            context.resume().catch(() => {})
            const samples = new Uint8Array(analyser.fftSize)
            let lastUpdate = 0
            let levels = Array(25).fill(0.06)
            const draw = (time) => {
                if (disposed) return
                if (time - lastUpdate >= 65) {
                    analyser.getByteTimeDomainData(samples)
                    let energy = 0
                    for (const sample of samples) energy += ((sample - 128) / 128) ** 2
                    const amplitude = Math.min(1, Math.max(0.06, Math.sqrt(energy / samples.length) * 8))
                    levels = [...levels.slice(1), amplitude]
                    setAudioLevels(levels)
                    lastUpdate = time
                }
                frame = requestAnimationFrame(draw)
            }
            frame = requestAnimationFrame(draw)
        } catch {
            // A visualization failure must not interrupt the audio recording.
        }
        return () => {
            disposed = true
            if (frame != null) cancelAnimationFrame(frame)
            source?.disconnect()
            if (context && context.state !== 'closed') context.close().catch(() => {})
        }
    }, [isRecording])

    useEffect(() => () => {
        if (audioUrl?.startsWith('blob:')) URL.revokeObjectURL(audioUrl)
    }, [audioUrl])

    useEffect(() => {
        activeRef.current = true
        return () => {
            activeRef.current = false
            clearInterval(recordingTimerRef.current)
            requestRef.current?.abort()
            const recorder = recorderRef.current
            if (recorder) {
                recorder.onstop = null
                if (recorder.state !== 'inactive') recorder.stop()
                recorder.stream.getTracks().forEach(track => track.stop())
            }
        }
    }, [])

    const startRecording = async () => {
        if (startingRef.current || recorderRef.current || processingAudio) return
        startingRef.current = true
        setIsStartingRecording(true)
        setRecordingError('')
        let stream
        try {
            stream = await navigator.mediaDevices.getUserMedia({ audio: true })
            if (!activeRef.current) {
                stream.getTracks().forEach(track => track.stop())
                return
            }
            audioRef.current?.pause()
            const mimeType = MediaRecorder.isTypeSupported('audio/mp4')
                ? 'audio/mp4'
                : MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')
                    ? 'audio/ogg;codecs=opus'
                    : ''
            const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {})
            const chunks = []
            recorder.ondataavailable = e => chunks.push(e.data)
            recorder.onstop = async () => {
                stream.getTracks().forEach(t => t.stop())
                const mime = recorder.mimeType || 'audio/mp4'
                const blob = new Blob(chunks, { type: mime })
                await sendAudio(blob)
            }
            recorder.start()
            recorderRef.current = recorder
            setIsRecording(true)
            setRecordingSeconds(0)
            recordingTimerRef.current = setInterval(() => {
                setRecordingSeconds(s => s + 1)
            }, 1000)
        } catch {
            stream?.getTracks().forEach(track => track.stop())
            if (activeRef.current) setRecordingError('Не удалось включить микрофон. Проверьте разрешение браузера.')
        } finally {
            startingRef.current = false
            if (activeRef.current) setIsStartingRecording(false)
        }
    }

    const stopRecording = () => {
        const recorder = recorderRef.current
        if (!recorder || recorder.state === 'inactive') return
        recorderRef.current = null
        recorder.stop()
        setIsRecording(false)
        clearInterval(recordingTimerRef.current)
        setProcessingAudio(true)
    }

    const sendAudio = async (blob) => {
        const controller = new AbortController()
        requestRef.current = controller
        try {
            const formData = new FormData()
            const ext = blob.type.includes('mp4') ? 'mp4' : blob.type.includes('ogg') ? 'ogg' : 'webm'
            formData.append('audio', blob, `audio.${ext}`)
            const res = await fetch(`/api/orders/${orderId}/voice`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData,
                signal: controller.signal,
            })
            const data = await res.json()
            if (res.ok && data.transcript?.trim()) {
                setAudioUrl(URL.createObjectURL(blob))
                onResult(data.transcript)
            } else {
                throw new Error(data.message || 'Не удалось распознать запись.')
            }
        } catch (err) {
            if (activeRef.current && err.name !== 'AbortError') setRecordingError(err.message || 'Ошибка отправки записи.')
        } finally {
            requestRef.current = null
            if (activeRef.current) setProcessingAudio(false)
        }
    }

    return {
        isRecording, audioLevels, isStartingRecording, processingAudio, recordingError,
        audioUrl, setAudioUrl,
        audioRef, recordingSeconds: formatSeconds(recordingSeconds),
        startRecording, stopRecording,
    }
}
